import {
	CreateTimereportRequest,
	UpdateTimereportRequest,
	SubmitTimereportsRequest,
	MonthReportSubmitRequest,
	CreateMonthReportsRequest,
	UpdateOvertimeChoiceRequest,
	VacationApplicationRequest,
} from '../Types/timereport';
import { useBizMutation, useBizQuery } from './useBizQuery';
import {
	getTimereports,
	getAbsenceRequests,
	getTimereport,
	createTimereport,
	createLeaveTimereports,
	updateTimereport,
	deleteTimereport,
	getActivities,
	getFavoriteActivities,
	setFavoriteActivity,
	submitTimereports,
	submitAllTimereports,
	getSubmitInfo,
	createMonthReports,
	submitWithMonthReport,
	attestTimereportEmployee,
	updateMonthReportOvertimeChoice,
	getHolidays,
	getWorkSchedule,
	getUserInfo,
	updateFlag,
	getVacationBalance,
	applyForVacation,
	getTimereportsForObject,
	getReminders,
	getTimereportTypes,
	getVacationApplications,
	getLeaveSettings,
	getMonthStats,
	getNormalizedHours,
	getAvailableLeaveBalance,
} from '../Services/timereport';

// Query key constants
const QUERYKEY_TIMEREPORTS = 'TIMEREPORTS';
const QUERYKEY_TIMEREPORT = 'TIMEREPORT';
const QUERYKEY_TIMEREPORT_ACTIVITIES = 'TIMEREPORT_ACTIVITIES';
const QUERYKEY_TIMEREPORT_FAVORITE_ACTIVITIES = 'TIMEREPORT_FAVORITE_ACTIVITIES';
const QUERYKEY_TIMEREPORT_SUBMIT_INFO = 'TIMEREPORT_SUBMIT_INFO';
const QUERYKEY_TIMEREPORT_HOLIDAYS = 'TIMEREPORT_HOLIDAYS';
const QUERYKEY_TIMEREPORT_USER_INFO = 'TIMEREPORT_USER_INFO';
const QUERYKEY_TIMEREPORT_VACATION_BALANCE = 'TIMEREPORT_VACATION_BALANCE';
const QUERYKEY_TIMEREPORT_OBJECT = 'TIMEREPORT_OBJECT';
const QUERYKEY_TIMEREPORT_TYPES = 'TIMEREPORT_TYPES';
const QUERYKEY_AVAILABLE_LEAVE_BALANCE = 'AVAILABLE_LEAVE_BALANCE';

// Time Reports

export const useGetTimereports = (start: string, end: string) => {
	const { data, statusState, refetch } = useBizQuery(
		[QUERYKEY_TIMEREPORTS, start, end],
		() => getTimereports(start, end),
		!!start && !!end
	);
	return { timereports: data, statusState, refetch };
};

export const useGetAbsenceRequests = (start: string, end: string) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_TIMEREPORTS, 'absence-requests', start, end],
		() => getAbsenceRequests(start, end),
		!!start && !!end
	);
	return { timereports: data, statusState };
};

export const useGetTimereport = (id: number) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_TIMEREPORT, id],
		() => getTimereport(id),
		id > 0
	);
	return { timereport: data, statusState };
};

export const useCreateTimereport = () => {
	const wrapperFn = (params: { data: CreateTimereportRequest }) => createTimereport(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS]],
	});
	return {
		create: (data: CreateTimereportRequest) => mutate({ data }),
		createAsync: (data: CreateTimereportRequest) => mutateAsync({ data }),
		createdTimereport: data,
		statusState,
	};
};

export const useCreateLeaveTimereport = () => {
	const wrapperFn = (params: { data: CreateTimereportRequest[] }) => createLeaveTimereports(params.data);
	const { mutate, statusState, data, errorType } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS], [QUERYKEY_AVAILABLE_LEAVE_BALANCE]],
	});
	return {
		create: (data: CreateTimereportRequest[]) => mutate({ data }),
		createdTimereports: data,
		statusState,
		errorType,
	};
};

export const useUpdateTimereport = () => {
	const wrapperFn = (params: { data: UpdateTimereportRequest }) => updateTimereport(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS], [QUERYKEY_TIMEREPORT]],
	});
	return {
		update: (data: UpdateTimereportRequest) => mutate({ data }),
		updateAsync: (data: UpdateTimereportRequest) => mutateAsync({ data }),
		updatedTimereport: data,
		statusState,
	};
};

export const useDeleteTimereport = () => {
	const wrapperFn = (params: { id: number }) => deleteTimereport(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS]],
	});
	return {
		remove: (id: number) => mutate({ id }),
		statusState,
	};
};

// Activities

export const useGetActivities = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_ACTIVITIES], getActivities);
	return { activities: data, statusState };
};

export const useGetFavoriteActivities = () => {
	const { data, statusState, refetch } = useBizQuery([QUERYKEY_TIMEREPORT_FAVORITE_ACTIVITIES], getFavoriteActivities);
	return { favorites: data, statusState, refetch };
};

export const useSetFavoriteActivity = () => {
	const wrapperFn = (params: { projectId: number; activityId: number; isFavorite: boolean }) =>
		setFavoriteActivity(params.projectId, params.activityId, params.isFavorite);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORT_FAVORITE_ACTIVITIES], [QUERYKEY_TIMEREPORT_ACTIVITIES]],
	});
	return {
		toggleFavorite: (projectId: number, activityId: number, isFavorite: boolean) =>
			mutate({ projectId, activityId, isFavorite }),
		statusState,
	};
};

// Submission

export const useSubmitTimereports = () => {
	const wrapperFn = (params: { data: SubmitTimereportsRequest }) => submitTimereports(params.data);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS]],
	});
	return {
		submit: (data: SubmitTimereportsRequest) => mutate({ data }),
		statusState,
	};
};

export const useSubmitAllTimereports = () => {
	const wrapperFn = (params: { upToDate: string }) => submitAllTimereports(params.upToDate);
	const { mutate, mutateAsync, statusState, data, reset } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS], [QUERYKEY_TIMEREPORT_USER_INFO]],
	});
	return {
		submitAll: (upToDate: string) => mutate({ upToDate }),
		submitAllAsync: (upToDate: string) => mutateAsync({ upToDate }),
		resetSubmitAll: reset,
		monthStats: data?.monthStats,
		savedHoursBefore: data?.savedHoursBefore ?? 0,
		monthReportUpToDate: data?.upToDate ?? '',
		statusState,
	};
};

export const useCreateMonthReports = () => {
	const wrapperFn = (params: { data: CreateMonthReportsRequest }) => createMonthReports(params.data);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS], [QUERYKEY_TIMEREPORT_SUBMIT_INFO], [QUERYKEY_TIMEREPORT_USER_INFO], [QUERYKEY_MONTH_STATS]],
	});
	return {
		createMonthReports: (data: CreateMonthReportsRequest) => mutate({ data }),
		statusState,
	};
	};

export const useGetSubmitInfo = (submitDate: string) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_TIMEREPORT_SUBMIT_INFO, submitDate],
		() => getSubmitInfo(submitDate),
		!!submitDate
	);
	return { submitInfo: data, statusState };
};

export const useSubmitWithMonthReport = () => {
	const wrapperFn = (params: { data: MonthReportSubmitRequest }) => submitWithMonthReport(params.data);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS], [QUERYKEY_TIMEREPORT_SUBMIT_INFO]],
	});
	return {
		submitMonth: (data: MonthReportSubmitRequest) => mutate({ data }),
		statusState,
	};
};

export const useAttestTimereportEmployee = () => {
	const wrapperFn = (params: { id: number }) => attestTimereportEmployee(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS]],
	});
	return {
		attestEmployee: (id: number) => mutate({ id }),
		statusState,
	};
};

export const useUpdateMonthReportOvertimeChoice = () => {
	const wrapperFn = (params: { data: UpdateOvertimeChoiceRequest[] }) => updateMonthReportOvertimeChoice(params.data);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORTS], [QUERYKEY_TIMEREPORT_SUBMIT_INFO], [QUERYKEY_TIMEREPORT_USER_INFO], [QUERYKEY_MONTH_STATS]],
	});
	return {
		updateOvertimeChoice: (data: UpdateOvertimeChoiceRequest[]) => mutate({ data }),
		statusState,
	};
};

// Holidays

export const useGetHolidays = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_HOLIDAYS], getHolidays);
	return { holidays: data, statusState };
};

// Timereport Types

export const useGetTimereportTypes = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_TYPES], getTimereportTypes);
	return { types: data, statusState };
};

// Work Schedule

const QUERYKEY_TIMEREPORT_WORK_SCHEDULE = 'TIMEREPORT_WORK_SCHEDULE';

export const useGetWorkSchedule = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_WORK_SCHEDULE], getWorkSchedule);
	return { workSchedule: data, statusState };
};

// User Info

export const useGetUserInfo = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_USER_INFO], getUserInfo);
	return { userInfo: data, statusState };
};

export const useUpdateFlag = () => {
	const wrapperFn = (params: { flagDate: string }) => updateFlag(params.flagDate);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORT_USER_INFO]],
	});
	return {
		updateFlagDate: (flagDate: string) => mutate({ flagDate }),
		statusState,
	};
};

// Vacation

export const useGetVacationBalance = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_VACATION_BALANCE], getVacationBalance);
	return { vacationBalance: data, statusState };
};

export const useApplyForVacation = () => {
	const wrapperFn = (params: { data: VacationApplicationRequest }) => applyForVacation(params.data);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TIMEREPORT_VACATION_BALANCE]],
	});
	return {
		apply: (data: VacationApplicationRequest) => mutate({ data }),
		statusState,
	};
};

// Object Link

export const useGetTimereportsForObject = (objectType: string, objectId: number) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_TIMEREPORT_OBJECT, objectType, objectId],
		() => getTimereportsForObject(objectType, objectId),
		!!objectType && objectId > 0
	);
	return { timereports: data, statusState };
};

// Reminders

const QUERYKEY_TIMEREPORT_REMINDERS = 'TIMEREPORT_REMINDERS';

export const useGetReminders = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TIMEREPORT_REMINDERS], getReminders);
	return { reminders: data, statusState };
};

// Vacation Applications

const QUERYKEY_VACATION_APPLICATIONS = 'VACATION_APPLICATIONS';

export const useGetVacationApplications = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_VACATION_APPLICATIONS], getVacationApplications);
	return { applications: data, statusState };
};

// Leave Settings

const QUERYKEY_LEAVE_SETTINGS = 'LEAVE_SETTINGS';

export const useGetLeaveSettings = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_LEAVE_SETTINGS], getLeaveSettings);
	return { leaveSettings: data, statusState };
};

export const useGetAvailableLeaveBalance = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_AVAILABLE_LEAVE_BALANCE], getAvailableLeaveBalance);
	return { availableBalance: data, statusState };
};

// Month Stats

const QUERYKEY_MONTH_STATS = 'MONTH_STATS';

export const useGetMonthStats = (from: string, to: string) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_MONTH_STATS, from, to],
		() => getMonthStats(from, to),
		!!from && !!to
	);
	return { monthStats: data, statusState };
};

const QUERYKEY_NORMALIZED_HOURS = 'NORMALIZED_HOURS';

export const useGetNormalizedHours = (from: string, to: string) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_NORMALIZED_HOURS, from, to],
		() => getNormalizedHours(from, to),
		!!from && !!to
	);
	return { normalizedHours: data?.normalizedHours ?? 0, statusState };
};
